import { useEffect, useRef } from "react";
import { rasterTileRegistry } from "../apis/RasterTileRegistry";
import { sharedPopupManager } from "../utils/popup/SharedPopupManager";

const useRasterTile = (
  mapRef,
  layerKey,
  opacity = 1,
  show = false,
  options = {},
) => {
  const isVisibleRef = useRef(show);
  const cleanupTimeoutRef = useRef(null);

  const config = rasterTileRegistry[layerKey];
  const isDynamic = config?.isDynamic;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !config) return;

    const { sourceId, layerId } = config;
    isVisibleRef.current = show;

    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }

    if (config.popup?.enabled) {
      sharedPopupManager.init(map);
    }

    const applyLayer = () => {
      if (!show) return;

      let tilesUrl = config.source.tiles[0];

      if (isDynamic && options) {
        const { country, date, time, utc, timezone, values } = options;
        const params = [];
        if (country) params.push(`country=${country}`);
        if (date) params.push(`date=${date}`);
        if (time) params.push(`time=${encodeURIComponent(time)}`);
        if (utc || timezone) params.push(`timezone=${utc || timezone}`);
        if (config.popup?.band) params.push(`band=${config.popup.band}`);
        if (values?.length) values.forEach((v) => params.push(`values=${v}`));
        if (params.length > 0) tilesUrl = `${tilesUrl}?${params.join("&")}`;
      }

      const existingSource = map.getSource(sourceId);
      if (!existingSource) {
        map.addSource(sourceId, { ...config.source, tiles: [tilesUrl] });
      } else {
        existingSource.setTiles([tilesUrl]);
      }

      if (!map.getLayer(layerId)) {
        const anchorLayer = options.priorityLayers?.find((id) =>
          map.getLayer(id),
        );

        map.addLayer(
          {
            id: layerId,
            source: sourceId,
            type: "raster",
            paint: { ...config.layer.paint, "raster-opacity": opacity },
          },
          anchorLayer || undefined,
        );

        if (options.priorityLayers) {
          options.priorityLayers.forEach((beforeLayerId) => {
            if (map.getLayer(beforeLayerId))
              map.moveLayer(layerId, beforeLayerId);
          });
        }
      } else {
        map.setLayoutProperty(layerId, "visibility", "visible");
        map.setPaintProperty(layerId, "raster-opacity", opacity);
      }

      if (config.popup?.enabled) {
        const fetchFn = async (lng, lat) => {
          let url = `${config.popup.endpoint}?lon=${lng}&lat=${lat}`;

          if (isDynamic && options) {
            const { country, date, time, utc, timezone } = options;
            if (country) url += `&country=${country}`;
            if (date) url += `&date=${date}`;
            if (time) url += `&time=${encodeURIComponent(time)}`;
            if (utc || timezone) url += `&timezone=${utc || timezone}`;
          }

          const res = await fetch(url);
          if (!res.ok) return null;

          const data = await res.json();
          const value = data?.value;

          if (typeof value !== "number") return null;

          return {
            label: config.popup.label,
            val: value.toFixed(2),
            unit: config.popup.unit,
          };
        };

        sharedPopupManager.register(layerKey, fetchFn);
      }
    };

    if (show) {
      applyLayer();
    } else {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, "visibility", "none");
      }

      if (config.popup?.enabled) {
        sharedPopupManager.unregister(layerKey);
      }

      cleanupTimeoutRef.current = setTimeout(() => {
        if (!isVisibleRef.current) {
          if (map.getLayer(layerId)) map.removeLayer(layerId);
          if (map.getSource(sourceId)) map.removeSource(sourceId);
        }
      }, 2000);
    }

    map.on("style.load", applyLayer);

    return () => {
      if (cleanupTimeoutRef.current) clearTimeout(cleanupTimeoutRef.current);
      if (config.popup?.enabled) sharedPopupManager.unregister(layerKey);
      map.off("style.load", applyLayer);
    };
  }, [mapRef, layerKey, show, ...(isDynamic ? [JSON.stringify(options)] : [])]);

  // Isolated opacity effect — only calls setPaintProperty, never reloads tiles
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !config || !show) return;
    const { layerId } = config;
    if (map.getLayer(layerId)) {
      map.setPaintProperty(layerId, "raster-opacity", opacity);
    }
  }, [opacity]);

  return null;
};

export default useRasterTile;